/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useTenantsColumns.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for defining tenants columns with custom renderers.
────────────────────────────────────────────────────────────────*/

import { useMemo, useCallback } from 'react';
import { Shield } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import type { Tenant } from '../types';

const BOOLEAN_BADGE_STYLES = {
  active: {
    true: 'bg-green-100 text-green-800',
    false: 'bg-red-100 text-red-800'
  }
} as const;

export function useTenantsColumns() {
  const { t, i18n, refreshKey } = useI18n();

  const availableColumns = useMemo(() => {
    return [
      { key: 'name', label: t('tenants.tenantName'), visible: true, sortable: true },
      { key: 'description', label: t('tenants.description'), visible: true, sortable: false },
      { key: 'defaultLocale', label: t('tenants.defaultLocale'), visible: true, sortable: true },
      { key: 'timezone', label: t('tenants.timezone'), visible: true, sortable: true },
      { key: 'isActive', label: t('tenants.enabled'), visible: true, sortable: true },
      { key: 'createdAt', label: t('tenants.createdAt'), visible: true, sortable: true },
      { key: 'actions', label: t('common.actions'), visible: true, sortable: false }
    ];
  }, [t, i18n.language, refreshKey]);

  const formatDate = useCallback(
    (date: string) =>
      new Date(date).toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-US'),
    [i18n.language]
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getColumnRenderers = useCallback((columns: Array<{ key: string; label: string; visible: boolean; sortable?: boolean }>): Array<{ key: string; label: string; visible: boolean; sortable: boolean; render?: (value: any, item: any) => React.ReactNode }> => {
    return columns.map(col => {
      const baseCol: { key: string; label: string; visible: boolean; sortable: boolean } = {
        key: col.key,
        label: col.label,
        visible: col.visible,
        sortable: Boolean(col.sortable ?? false)
      };
      if (baseCol.key === 'name') {
        return {
          ...baseCol,
          render: (value: string, item: Tenant) => (
            <div className="flex items-center gap-2">
              <span className="font-medium text-foreground">{value}</span>
              {item.isPrimary && (
                <span
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border border-amber-200 dark:border-amber-800"
                  title={t('tenants.primaryTenant')}
                >
                  <Shield className="h-2.5 w-2.5" />
                  {t('tenants.main')}
                </span>
              )}
            </div>
          )
        };
      }

      if (baseCol.key === 'isActive') {
        return {
          ...baseCol,
          render: (value: boolean) => (
            <span
              className={`inline-flex items-center rounded-full px-2 py-1 text-xs ${BOOLEAN_BADGE_STYLES.active[value ? 'true' : 'false']
                }`}
            >
              {value ? t('common.active') : t('common.inactive')}
            </span>
          )
        };
      }

      if (baseCol.key === 'createdAt') {
        return {
          ...baseCol,
          render: (value: string) => (
            <div className="text-sm text-muted-foreground">{formatDate(value)}</div>
          )
        };
      }

      return baseCol;
    });
  }, [t, formatDate]);

  return {
    availableColumns,
    getColumnRenderers
  };
}

